import { inject, Injectable, Provider } from '@angular/core'

import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  from,
  map,
  Observable,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs'

import {
  Action,
  DataQueryProps,
  Resource,
  Role,
  RoleRange,
  TSP_CUSTOM_ROLE_ID_RANGE,
  TSP_ROLE_ID_RANGE,
  UserProfile,
  UserProfileTiView,
  UserRole,
} from '@ti/contracts'
import { ExportFormat } from '@ti/core/common/contracts'
import { Users1IconComponent } from '@ti/core/icons/users-1'
import { ListModelConfig } from '@ti/core/models/types'
import { BaseUsersModel, UserGridData } from '@ti/shared/user'

import { TspFleetUserManager } from '../services/user.manager'
import { TspFleetUsersStore, TspUsersStore } from '../stores'

@Injectable()
export class TspUsersModel extends BaseUsersModel {
  protected readonly modelName = 'TspUsersModel'
  protected readonly usersStore = inject(TspUsersStore)
  protected override readonly roleIdRanges: RoleRange[] = [
    TSP_ROLE_ID_RANGE,
    TSP_CUSTOM_ROLE_ID_RANGE,
  ]

  protected loadPage(props: DataQueryProps): Observable<UserProfile[]> {
    return this.api.tsp.user
      .listTsp$(props)
      .pipe(tap((users) => this.usersStore.merge(undefined, users)))
  }

  protected async countAllUsers() {
    const options = { search: '' }
    return this.api.tsp.user.countTsp(options)
  }

  protected override config(): ListModelConfig {
    return {
      ...super.config(),
      exportReport: (format, fileName, props) => {
        return firstValueFrom(this.api.tsp.user.exportList$(format, fileName, props))
      },
    }
  }

  protected override mapUserData(
    user: UserProfileTiView,
    roles: Role[],
    roleLabels: Record<UserRole, string>,
  ) {
    return {
      ...super.mapUserData(user, roles, roleLabels),
      auditId: user.auditId ?? undefined,
    }
  }

  private withExtension(fileName: string, format: ExportFormat): string {
    const clean = fileName.replace(/\.(csv|xlsx|pdf)$/i, '')
    return `${clean}.${format}`
  }
}

@Injectable()
export class TspFleetUsersModel extends BaseUsersModel {
  protected readonly modelName = 'TspFleetUsersModel'
  protected readonly usersStore = inject(TspFleetUsersStore)
  protected readonly selectedFleet$ = new BehaviorSubject<number>(0)

  public filterByFleet(id: number) {
    this.selectedFleet$.next(id)
  }

  protected loadPage(props: DataQueryProps): Observable<UserProfile[]> {
    const fleetId = this.selectedFleet$.value
    if (!fleetId) {
      return from(Promise.resolve([]))
    }
    return this.api.tsp.fleetUser
      .list$(fleetId, props)
      .pipe(tap((users) => this.usersStore.merge(fleetId, users)))
  }

  protected async countAllUsers() {
    const fleetId = this.selectedFleet$.value
    if (!fleetId) return 0

    return this.api.tsp.fleetUser.count(fleetId, { search: '' })
  }

  protected override config(): ListModelConfig {
    return {
      ...super.config(),
      exportReport: (format, fileName, props) => {
        const fleetId = this.selectedFleet$.value
        if (!fleetId) {
          return Promise.resolve()
        }
        return firstValueFrom(
          this.api.tsp.fleetUser.exportList$(
            fleetId,
            format,
            this.withExtension(fileName, format),
            props,
          ),
        )
      },
    }
  }

  protected override getOptionsStream(data: UserGridData) {
    return combineLatest([
      this.profile.state,
      this.accessControl.check$([Resource.Fleet, Action.Update]),
    ]).pipe(
      map(([, canUpdateFleet]) => {
        const isOwner = data.user.roleId === UserRole.FleetOwner
        return [
          {
            label: 'users.grid.edit-btn',
            testId: 'users.grid.edit-btn',
            icon: 'pi pi-pencil',
            styleClass: `${canUpdateFleet ? '' : 'not-allowed'}`,
            command: () => (canUpdateFleet ? this.select(data.user) : undefined),
          },
          {
            label: 'users.grid.transfer-ownership',
            testId: 'users.grid.transfer-ownership',
            iconComponent: Users1IconComponent,
            disabled: !canUpdateFleet || isOwner,
            styleClass: `has-bottom-border  ${canUpdateFleet && !isOwner ? '' : 'not-allowed'}`,
            command: () =>
              canUpdateFleet && !isOwner
                ? (this.userManager as TspFleetUserManager).transferOwnership(data.user)
                : undefined,
          },
          {
            label: 'users.grid.delete-btn',
            testId: 'users.grid.delete-btn',
            icon: 'pi pi-trash',
            disabled: !canUpdateFleet || isOwner,
            styleClass: `error ${canUpdateFleet && !isOwner ? '' : 'not-allowed'}`,
            command: () =>
              canUpdateFleet && !isOwner ? this.userManager.delete(data.user) : undefined,
          },
        ]
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  private withExtension(fileName: string, format: ExportFormat): string {
    const clean = fileName.replace(/\.(csv|xlsx|pdf)$/i, '')
    return `${clean}.${format}`
  }
}

export const provideTspUsersModel = (): Provider[] => {
  return [TspUsersModel, { provide: BaseUsersModel, useExisting: TspUsersModel }]
}

export const provideTspFleetUsersModel = (): Provider[] => {
  return [TspFleetUsersModel, { provide: BaseUsersModel, useExisting: TspFleetUsersModel }]
}
